/**
 * Primitive palettes (docs/06-design-system.md › Color primitives).
 * Brand hues: berry (warm red), blue (anchored on #2AA0FF), gold.
 * Supporting hues (mint, orange, crimson, plum) are for status colors and occasion kinds only.
 *
 * Components never use these directly; they use the semantic tokens in semantic.ts.
 */
export const palette = {
  neutral: {
    0: '#FFFFFF',
    25: '#FFFCF7',
    50: '#FFF7EC',
    100: '#F6EEE2',
    200: '#EADFD0',
    300: '#D5C8B6',
    400: '#B0A28F',
    500: '#8A7D6C',
    600: '#6A5E50',
    700: '#4D4339',
    800: '#332C25',
    900: '#221D18',
    950: '#15110D',
  },
  berry: {
    50: '#FFF0F1',
    100: '#FFD9DD',
    200: '#FFB3BC',
    300: '#FF8595',
    400: '#F2566D',
    500: '#D12E45',
    600: '#B0223A',
    700: '#8C1B2F',
    800: '#661523',
    900: '#420E17',
  },
  blue: {
    50: '#EAF6FF',
    100: '#CCE9FF',
    200: '#99D3FF',
    300: '#66BDFF',
    400: '#45AEFF',
    500: '#2AA0FF',
    600: '#0F84E0',
    700: '#0A67B3',
    800: '#084C85',
    900: '#06325A',
  },
  gold: {
    50: '#FFF8E6',
    100: '#FFEDBF',
    200: '#FFDD85',
    300: '#FFCD52',
    400: '#FFC23D',
    500: '#F0A818',
    600: '#C98A0A',
    700: '#9A6908',
    800: '#6B4906',
    900: '#3F2B04',
  },
  mint: {
    50: '#E9FAF1',
    100: '#C8F1DB',
    300: '#6FD6A3',
    500: '#1FA566',
    700: '#137046',
    900: '#0A3D26',
  },
  orange: {
    50: '#FFF3E6',
    100: '#FFE0BF',
    300: '#FFA95C',
    500: '#E8740C',
    700: '#9E4C05',
    900: '#522702',
  },
  crimson: {
    50: '#FDECEC',
    100: '#F9CFCF',
    300: '#EE7F7F',
    500: '#B3261E',
    700: '#7F1A15',
    900: '#450D0A',
  },
  plum: { 100: '#EFE0FA', 300: '#C79BEB', 500: '#8E4FC7', 700: '#5E2E8A' },
} as const;

export type Palette = typeof palette;
export type HueName = keyof Palette;

/** A reference to a primitive, e.g. `'berry.500'`, or a literal hex for rare one-offs. */
export type ColorRef =
  { [H in HueName]: `${H}.${keyof Palette[H] & (string | number)}` }[HueName] | `#${string}`;

export const hueDescriptions: Record<HueName, string> = {
  neutral: 'Warm cream-to-ink neutrals. Backgrounds, text, borders.',
  berry: 'Warm red. Primary actions and big happy moments.',
  blue: 'Anchored on #2AA0FF. Interactive and "you" moments. As text, only 700+ on light backgrounds.',
  gold: 'Sunny highlights, most-wanted stars. Always a fill or icon, never text on light.',
  mint: 'Success, and Christmas.',
  orange: 'Warnings, and the "other" occasion kind.',
  crimson: 'Destructive and error states. Distinct from berry.',
  plum: 'Anniversaries.',
};

/** Resolve a ColorRef to its hex value. */
export function resolveColor(ref: ColorRef): string {
  if (ref.startsWith('#')) return ref;
  const [hue, step] = ref.split('.') as [HueName, string];
  const value = (palette[hue] as Record<string, string>)[step];
  if (!value) throw new Error(`Unknown color ref: ${ref}`);
  return value;
}
